'use client';
import { config } from '@/configs/configPortal';
import { handleDispatchInteractionEvent } from '@/helpers/adobe';
import { formatToPrice } from '@/helpers/utils';
import clsx from 'clsx';
import Image from 'next/image';
import { useContext, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ResultContext } from '../pages/result/result.context';
import ChartButton from './chart-button';
import './chart-history-price.scss';
import useChartData from './use-chart-data.hook';
import useDisabledStates from './use-disabled-states.hook';

export type HistoricOption = { last_price: number; ref_month: string };

export type ChartProps = {
    historic: {
        fipe: {
            used: HistoricOption[];
            new: HistoricOption[];
        };
        wm_average: {
            armored: HistoricOption[];
            not_armored: HistoricOption[];
        };
    };
};

export enum EPriceType {
    fipe = 1,
    webmotors = 2,
}

export enum ETime {
    six = 6,
    twelve = 12,
}

export default function ChartComponent({ historic }: ChartProps) {
    const { isVehicle0km, isVehicleArmored } = useContext(ResultContext);
    const [priceType, setPriceType] = useState<EPriceType>(EPriceType.fipe);
    const [time, setTime] = useState<ETime>(ETime.six);
    const { data, source } = useChartData(historic, priceType, time, isVehicle0km, isVehicleArmored);
    const { isDisabled } = useDisabledStates(historic, isVehicle0km, isVehicleArmored);
    const chartImage = `${config.urlTableFipe}/assets/img/chart-${priceType === EPriceType.fipe ? 'fipe' : 'webmotors'}.svg`;
    const isFipe = priceType === EPriceType.fipe;

    const hasDataToShowGraph = isVehicleArmored && historic?.wm_average?.armored?.length > 0
        || isVehicle0km && historic?.fipe?.new?.length > 0
        || historic?.fipe?.used?.length > 0
        || historic?.wm_average?.not_armored?.length > 0;

    useEffect(() => {
        setPriceType(() => {
            if (isVehicle0km) {
                return isDisabled.fipe ? EPriceType.webmotors : EPriceType.fipe;
            }
            if (isVehicleArmored) {
                return !isDisabled.webmotors ? EPriceType.webmotors : EPriceType.fipe;
            }
            return isDisabled.fipe && !isDisabled.webmotors ? EPriceType.webmotors : EPriceType.fipe;
        });
    }, [isDisabled.fipe, isDisabled.webmotors, isVehicle0km, isVehicleArmored]);

    useEffect(() => {
        if (source?.length <= 6) {
            setTime(ETime.six);
        }
    }, [priceType]);

    if (isDisabled.all || (isVehicleArmored && isDisabled.webmotors) || (isVehicle0km && isDisabled.fipe)) return null;

    const getPriceTypeImage = (isActive: boolean, disabled: boolean) => {
        if (isActive) {
            return (
                <Image
                    src={`${config.urlTableFipe}/assets/img/icon/icon-graph.svg`}
                    width={17}
                    height={16}
                    alt="icon grafico"
                    loading="lazy"
                    data-testid="icon-graph-active"
                    id="icon-graph-active"
                />
            );
        } else if (disabled) {
            return (
                <Image
                    src={`${config.urlTableFipe}/assets/img/icon/icon-graph-disabled.svg`}
                    width={17}
                    height={16}
                    alt="icon grafico desativado"
                    loading="lazy"
                    data-testid="icon-graph-disabled"
                    id="icon-graph-disabled"
                />
            );
        }

        return (
            <Image
                src={`${config.urlTableFipe}/assets/img/icon/icon-graph-available.svg`}
                width={17}
                height={16}
                alt="icon grafico disponivel"
                loading="lazy"
                data-testid="icon-graph-available"
                id="icon-graph-available"
            />
        );
    };

    return (
        <section
            className={`chart-history-price ${hasDataToShowGraph ? '' : 'hidden'}`}
            data-testid="chart-history-price-section"
            id="chart-history-price-section"
        >
            <div
                className="chart-history-price__title"
                data-testid="chart-history-price-title"
                id="chart-history-price-title"
            >
                <h2>Histórico de preços nos últimos meses</h2>
            </div>

            <div
                className="chart-history-price__price-type"
                data-testid="chart-history-price-type"
                id="chart-history-price-type"
            >
                <ChartButton
                    label={
                        <>
                            {getPriceTypeImage(isFipe, isDisabled.fipe)}
                            Tabela Fipe
                        </>
                    }
                    active={isFipe}
                    onClick={() => handleDispatchInteractionEvent('historico-tabela-fipe-resultado', () => setPriceType(EPriceType.fipe))}
                    disabled={isDisabled.fipe}
                    data-testid="chart-button-fipe"
                    id="chart-button-fipe"
                />
                <ChartButton
                    label={
                        <>
                            {getPriceTypeImage(!isFipe, isDisabled.webmotors)}
                            Tabela Webmotors
                        </>
                    }
                    active={!isFipe}
                    onClick={() => handleDispatchInteractionEvent('historico-tabela-webmotors-resultado', () => setPriceType(EPriceType.webmotors))}
                    disabled={isDisabled.webmotors}
                    data-testid="chart-button-webmotors"
                    id="chart-button-webmotors"
                />
            </div>

            <div
                className="chart-history-price__content"
                data-testid="chart-history-price-content"
                id="chart-history-price-content"
            >
                <div
                    className="chart-history-price__chart-header"
                    data-testid="chart-history-price-chart-header"
                    id="chart-history-price-chart-header"
                >
                    <div
                        className="chart-history-price__time"
                        data-testid="chart-history-price-time"
                        id="chart-history-price-time"
                    >
                        <ChartButton
                            label="6 meses"
                            active={time === ETime.six}
                            onClick={() => handleDispatchInteractionEvent('historico-6-meses-resultado', () => setTime(ETime.six))}
                            disabled={false}
                            data-testid="chart-button-6-months"
                            id="chart-button-6-months"
                        />
                        <ChartButton
                            label="12 meses"
                            active={time === ETime.twelve}
                            onClick={() => handleDispatchInteractionEvent('historico-12-meses-resultado', () => setTime(ETime.twelve))}
                            disabled={source?.length <= 6}
                            data-testid="chart-button-12-months"
                            id="chart-button-12-months"
                        />
                    </div>

                    <div
                        className="chart-history-price__logo"
                        data-testid="chart-history-price-logo"
                        id="chart-history-price-logo"
                    >
                        <Image
                            src={chartImage}
                            className={clsx(isFipe ? 'fipe' : 'webmotors')}
                            width={isFipe ? 56 : 172}
                            height={32}
                            alt={`logo ${isFipe ? 'fipe' : 'webmotors'}`}
                            loading="lazy"
                        />
                    </div>
                </div>

                <div
                    className="chart-history-price__chart"
                    data-testid="chart-history-price-chart"
                    id="chart-history-price-chart"
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            id="chart-line-chart"
                            data={data}
                            margin={{ top: 6, right: 6, bottom: 6, left: isMobile ? 40 : 70 }}
                            data-testid="line-chart"
                        >
                            <CartesianGrid
                                stroke="#EDECF2"
                                strokeDasharray="10"
                                vertical={false}
                                data-testid="cartesian-grid"
                            />
                            <XAxis
                                stroke="#EDECF2"
                                dataKey="name"
                                tick={{ fill: '#6A6977', ...{ angle: isMobile ? -30 : 0 } }}
                                strokeWidth={2}
                                padding={isMobile ? { left: 20, right: 20 } : { left: 130, right: 130 }}
                                tickMargin={isMobile ? 10 : 0}
                                fontSize={12}
                                data-testid="x-axis"
                            />
                            <YAxis
                                tickFormatter={(value) => `${formatToPrice(value)}`}
                                padding={isMobile ? { top: 0, bottom: 20 } : { top: 0, bottom: 50 }}
                                stroke="0"
                                tick={{ fill: '#6A6977' }}
                                fontSize={12}
                                domain={['auto', 'auto']}
                                data-testid="y-axis"
                            />
                            <Tooltip
                                formatter={(value) => [`${formatToPrice(value.toString())}`, '']}
                                separator=""
                                wrapperClassName="chart-history-price__tooltip"
                                labelClassName="chart-history-price__tooltip-label"
                                contentStyle={{ fontWeight: 700 }}
                                cursor={{ stroke: '#EDECF2', strokeWidth: 1 }}
                                data-testid="tooltip"
                            />
                            <Line
                                type="monotone"
                                dataKey="uv"
                                stroke="#5858BF"
                                dot={{ strokeWidth: 1 }}
                                activeDot={{ r: 6, strokeWidth: 0 }}
                                strokeWidth={2}
                                r={6}
                                className="chart-history-price__line"
                                data-testid="line"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </section>
    );
}
